import {useEffect, useState, Fragment} from 'react'
import {ExamDelete, ExamSave} from "services/examService"
import {VehicleGetPaging} from "services/vehicleService"
import {Dialog, Transition} from '@headlessui/react'
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import ErrorModal from "popup/ErrorModal"
import ConfirmDialog from "popup/ConfirmDialog"
import {toast} from "react-hot-toast"

interface SaveExamModalProps {
    isOpen: boolean
    onClose: () => void
    courseId: string
    exam?: any | null
    refreshData: () => void
}

export default function SaveExamModal({isOpen, onClose, courseId, exam, refreshData}: SaveExamModalProps) {
    const [vehicles, setVehicles] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [deleteId, setDeleteId] = useState<string | null>(null)
    const [formData, setFormData] = useState({
        name: '',
        type: 1,
        vehicleId: '',
        startDate: new Date(),
        endDate: new Date(),
    })

    const [errorModalOpen, setErrorModalOpen] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        if (isOpen) {
            fetchVehicles()
            if (exam) {
                setFormData({
                    name: exam.name,
                    type: exam.type,
                    vehicleId: exam.vehicleId || '',
                    startDate: new Date(exam.startDate),
                    endDate: new Date(exam.endDate),
                })
                setDeleteId(exam.id)
            } else {
                setFormData({
                    name: '',
                    type: 1,
                    vehicleId: '',
                    startDate: new Date(),
                    endDate: new Date(),
                })
            }
        }
    }, [isOpen, exam])

    const fetchVehicles = async () => {
        try {
            const res = await VehicleGetPaging({courseId})
            setVehicles(res.result.items)
        } catch (error) {
            console.error('Failed to fetch vehicles:', error)
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target
        setFormData(prev => ({
            ...prev,
            [name]: name === 'type' ? parseInt(value) : value
        }))
    }

    const handleDateChange = (name: string, date: Date) => {
        setFormData(prev => ({...prev, [name]: date}))
    }

    const handleDelete = async () => {
        if (!deleteId) {
            setErrorMessage('Không tìm thấy ID kỳ thi để xóa');
            setErrorModalOpen(true);
            return;
        }
        setShowDeleteConfirm(true);
    }

    const confirmDelete = async () => {
        setIsLoading(true)
        try {
            await ExamDelete(deleteId || '');
            refreshData();
            toast.success('Xóa kỳ thi thành công');
            onClose();
        } catch (error) {
            console.error('Failed to delete exam:', error);
            setErrorMessage('Xóa kỳ thi thất bại. Vui lòng thử lại sau.');
            setErrorModalOpen(true);
        } finally {
            setIsLoading(false);
            setShowDeleteConfirm(false);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)
        try {
            const adjustTimezone = (date: Date) => {
                const newDate = new Date(date)
                newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset())
                return newDate
            }

            const payload = {
                id: exam?.id || null,
                courseId,
                name: formData.name,
                type: formData.type,
                vehicleId: formData.vehicleId || null,
                startDate: adjustTimezone(formData.startDate),
                endDate: adjustTimezone(formData.endDate),
            };

            const response = await ExamSave(payload);

            refreshData();
            toast.success(exam ? 'Cập nhật kỳ thi thành công' : 'Thêm kỳ thi mới thành công');
            onClose();
        } catch (error: any) {
            const errorMsg = error?.responseException?.exceptionMessage
                || error?.message
                || 'Đã xảy ra lỗi không xác định';
            setErrorMessage(errorMsg);
            setErrorModalOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    const examTypes = [
        {value: 1, label: 'Thi lý thuyết'},
        {value: 2, label: 'Thi thực hành'},
        {value: 3, label: 'Thi mô phỏng'},
        {value: 4, label: 'Thi đường trường'},
        {value: 5, label: 'Thi tốt nghiệp'},
        {value: 6, label: 'Thi sát hạch'},
        {value: 7, label: 'Thi khác'},
    ];

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm"/>
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel
                                    className="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        {exam ? 'Cập nhật kỳ thi' : 'Thêm kỳ thi mới'}
                                    </Dialog.Title>

                                    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Tên kỳ thi
                                                *</label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                                disabled={isLoading}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Loại thi
                                                *</label>
                                            <select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                                disabled={isLoading}
                                            >
                                                {examTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Phương
                                                tiện</label>
                                            <select
                                                name="vehicleId"
                                                value={formData.vehicleId}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                disabled={isLoading}
                                            >
                                                <option value="">Không chọn</option>
                                                {vehicles.map(vehicle => (
                                                    <option key={vehicle.id} value={vehicle.id}>
                                                        {vehicle.plateNumber}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày bắt đầu
                                                *</label>
                                            <DatePicker
                                                selected={formData.startDate}
                                                // @ts-ignore
                                                onChange={(date: Date) => handleDateChange('startDate', date)}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                                calendarStartDay={1}
                                                disabled={isLoading}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Ngày kết
                                                thúc *</label>
                                            <DatePicker
                                                selected={formData.endDate}
                                                // @ts-ignore
                                                onChange={(date: Date) => handleDateChange('endDate', date)}
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="dd/MM/yyyy HH:mm"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                                calendarStartDay={1}
                                                disabled={isLoading}
                                            />
                                        </div>

                                        <div className="flex justify-between pt-4">
                                            {exam && (
                                                <button
                                                    type="button"
                                                    onClick={handleDelete}
                                                    disabled={isLoading || !deleteId}
                                                    className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 ${isLoading || !deleteId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    Xóa
                                                </button>
                                            )}
                                            <div></div>
                                            <div className="flex justify-end space-x-3">
                                                <button
                                                    type="button"
                                                    onClick={onClose}
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                                                >
                                                    Hủy
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={isLoading}
                                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isLoading ? (
                                                        <span className="flex items-center">
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                                 xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                 viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                                        stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor"
                                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            {exam ? 'Đang cập nhật...' : 'Đang thêm...'}
                                                        </span>
                                                    ) : exam ? 'Cập nhật' : 'Thêm mới'}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Xác nhận xóa kỳ thi"
                description="Bạn có chắc chắn muốn xóa kỳ thi này?"
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={() => confirmDelete()}
            />

            <ErrorModal
                isOpen={errorModalOpen}
                message={errorMessage}
                onClose={() => setErrorModalOpen(false)}
            />
        </>
    )
}